import { createColumnHelper } from '@tanstack/react-table'
import { ReactNode, useMemo } from 'react'
import { ColumnDef } from '@atbay/cyber-components'
import Typography from '@/ui/components/typography'
import {
    Cell,
    IssueCountItem,
    TableWrapper,
} from './CloudAppIntegrationsTable.styles'
import { StyledCustomTable } from '@/ui/components/CustomTable/CustomTable.styles'
import { IntegrationStatus } from '@/ui/routes/integrations/integration/IntegrationsTable/IntegrationStatus'
import { useCustomTheme } from '@/core/theme'
import {
    useCloudAppIntegrationsTable,
    TCloudAppIntegrationRow,
} from './useCloudAppIntegrationsTable'
import { getDateString } from '@/ui/utils/utils'
import Microsoft365IntegrationIcon from '@/assets/images/microsoft-integration.svg?react'
import GoogleIntegrationIcon from '@/assets/images/google-workspace-fd.svg?react'
import { TAllIntegrations } from '@/core/modules/integrations/integrations'
import { SEVERITY_LEVELS } from '@/core/modules/issues/constants'
import Show from '@/ui/components/helpers/Show'
import SeverityIcon from '@/ui/components/svg/SeverityIcon'
import { CloudAppIntegrationsEmptyState } from './CloudAppIntegrationsEmptyState'
import { SkeletonTable } from '@components/skeleton-table'

const PROVIDER_ICONS: Partial<Record<TAllIntegrations, ReactNode>> = {
    google: <GoogleIntegrationIcon width={120} height={120} />,
    microsoft: <Microsoft365IntegrationIcon width={110} height={110} />,
}

const columnHelper = createColumnHelper<TCloudAppIntegrationRow>()

const CLOUD_APP_SKELETON_COLUMN_COUNT = 5
const CLOUD_APP_SKELETON_ROW_COUNT = 2

const CloudAppIntegrationsTable = () => {
    const theme = useCustomTheme()
    const { rows, handleRowClick, activeRowId, isAssetsLoading } =
        useCloudAppIntegrationsTable()

    const columns: ColumnDef<TCloudAppIntegrationRow>[] = useMemo(
        () => [
            {
                ...columnHelper.accessor('name', {
                    cell: (info) => {
                        const { type } = info.row.original
                        return (
                            <Cell
                                data-testid={`cloud-app-integrations-table-service-${type}`}
                                onClick={() =>
                                    handleRowClick(info.row, info.table)
                                }
                            >
                                {PROVIDER_ICONS[type]}
                            </Cell>
                        )
                    },
                    header: () => (
                        <Typography variant="s-regular">Service</Typography>
                    ),
                    enableSorting: false,
                }),
                width: '20%',
            },

            {
                ...columnHelper.accessor('external_identifier', {
                    cell: (info) => (
                        <Cell
                            data-testid="cloud-app-integrations-table-connected-account"
                            onClick={() => handleRowClick(info.row, info.table)}
                        >
                            <Typography
                                variant="s-medium"
                                color={theme.colors.greys[700]}
                            >
                                {info.cell.getValue()}
                            </Typography>
                        </Cell>
                    ),
                    header: () => (
                        <Typography variant="s-regular">
                            Connected Account
                        </Typography>
                    ),
                    enableSorting: false,
                }),
                width: '25%',
            },

            {
                ...columnHelper.accessor('last_seen', {
                    cell: (info) => {
                        const lastSeen = info.cell.getValue()
                        return (
                            <Cell
                                data-testid="cloud-app-integrations-table-last-sync"
                                onClick={() =>
                                    handleRowClick(info.row, info.table)
                                }
                            >
                                <Typography
                                    variant="s-medium"
                                    color={theme.colors.greys[700]}
                                >
                                    {getDateString(lastSeen ?? '')}
                                </Typography>
                            </Cell>
                        )
                    },
                    header: () => (
                        <Typography variant="s-regular">Last Seen</Typography>
                    ),
                    enableSorting: false,
                }),
                width: '20%',
            },

            {
                ...columnHelper.accessor('status', {
                    cell: (info) => (
                        <Cell
                            data-testid={`cloud-app-integrations-table-status-${info.cell.getValue()}`}
                            onClick={() => handleRowClick(info.row, info.table)}
                        >
                            <IntegrationStatus
                                status={info.cell.getValue()}
                                type={info.row.original.type}
                            />
                        </Cell>
                    ),
                    header: () => (
                        <Typography variant="s-regular">Status</Typography>
                    ),
                    enableSorting: false,
                }),
                width: '15%',
            },

            {
                ...columnHelper.display({
                    id: 'numberOfOpenIssuesBySeverity',
                    cell: (info) => {
                        const { numberOfOpenIssuesBySeverity } =
                            info.row.original
                        return (
                            <Cell
                                data-testid="cloud-app-integrations-table-open-issues"
                                onClick={() =>
                                    handleRowClick(info.row, info.table)
                                }
                            >
                                {SEVERITY_LEVELS.map((severity) => (
                                    <Show
                                        key={severity}
                                        when={
                                            numberOfOpenIssuesBySeverity?.[
                                                severity
                                            ]
                                        }
                                    >
                                        <IssueCountItem data-testid="cloud-app-issues-severity-count">
                                            <SeverityIcon
                                                severity={severity}
                                                dataTestId={`severity-${severity}`}
                                            />
                                            <Typography variant="s-regular">
                                                {
                                                    numberOfOpenIssuesBySeverity?.[
                                                        severity
                                                    ]
                                                }
                                            </Typography>
                                        </IssueCountItem>
                                    </Show>
                                ))}
                            </Cell>
                        )
                    },
                    header: () => (
                        <Typography variant="s-regular">Open Issues</Typography>
                    ),
                }),
                width: '20%',
            },
        ],
        [handleRowClick, theme]
    )

    if (isAssetsLoading) {
        return (
            <SkeletonTable
                columnCount={CLOUD_APP_SKELETON_COLUMN_COUNT}
                rowCount={CLOUD_APP_SKELETON_ROW_COUNT}
                testIdPrefix="cloud-app-integrations-table-skeleton"
            />
        )
    }

    if (rows.length === 0) {
        return <CloudAppIntegrationsEmptyState />
    }

    return (
        <TableWrapper data-testid="cloud-app-integrations-table">
            <StyledCustomTable
                columns={columns}
                data={rows}
                setSorting={() => {}}
                sorting={[]}
                activeRow={activeRowId}
                getRowId={(row: TCloudAppIntegrationRow) => row.id}
            />
        </TableWrapper>
    )
}

export default CloudAppIntegrationsTable
